import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { providers as providersApi, claims } from '../services/api';

const ClaimListing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProvider = async () => {
      try {
        const response = await providersApi.getById(id);
        setProvider(response.data);
        if (response.data.is_claimed) {
          setError('This listing has already been claimed.');
        }
      } catch (err) {
        setError('Listing not found.');
      } finally {
        setLoading(false);
      }
    };
    loadProvider();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await claims.request({ provider_id: id, email });
      setResult(response.data);

      if (response.data.status === 'approved' && response.data.reset_token) {
        // Auto-approved — redirect to set password
        setTimeout(() => {
          navigate(`/reset-password?token=${response.data.reset_token}`);
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit claim.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
        <Link to="/" className="text-purple-600 hover:text-purple-800 text-sm mb-6 block">&larr; Back to directory</Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Claim Your Listing</h1>

        {provider && !result && (
          <>
            <p className="text-gray-600 mb-6">
              Claiming <strong>{provider.business_name}</strong>? Enter the email address associated with your business.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {!provider.is_claimed && (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Claim This Listing'}
                </button>
              </form>
            )}
          </>
        )}

        {result && (
          <div className={`mt-4 p-4 rounded-lg ${result.status === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
            {result.status === 'approved' ? (
              <>
                <h3 className="text-green-800 font-semibold mb-2">Claim Approved!</h3>
                <p className="text-green-700">You'll be redirected to set your password shortly...</p>
              </>
            ) : (
              <>
                <h3 className="text-blue-800 font-semibold mb-2">Claim Submitted</h3>
                <p className="text-blue-700">{result.message}</p>
              </>
            )}
          </div>
        )}

        {error && !provider && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimListing;
